// The sidebar's remembered collapse (#7710; owner ruling, #6552 item 4): at `lg`
// and up the rail can collapse to a narrow icon rail and expand back, and the
// choice is remembered per browser.
//
// One browser-local value, `dz-rail-collapsed` = a boolean, read and written ONLY
// through `local-pref.ts` (try/catch + schema + fallback). Nothing stored, a value
// the schema refuses, or a store that throws (the methods, or the `localStorage`
// property itself in a private window) all read as `false`, the expanded rail;
// a write that throws is dropped, so the toggle still works in memory for the
// session and only the reload forgets it.
//
// GLOBAL, not org-scoped, like `dz-rail-folds`: it is a preference about the
// chrome, not account data. Written on the mutation, never from an effect.
//
// Below `lg` the sidebar is the off-canvas drawer, which always shows its full
// labels: `collapsed()` is `false` there whatever is stored, and `toggle()` is a
// no-op that neither flips nor writes the value. Created per mount (`AppShell`),
// because the viewport query adds a listener its owner must remove.

import { createSignal } from 'solid-js';
import { z } from 'zod';
import { readLocalPref, writeLocalPref } from './local-pref';
import { createRailViewport } from './viewport';

export const RAIL_COLLAPSE_KEY = 'dz-rail-collapsed';

const RailCollapsedSchema = z.boolean();

export type RailCollapse = {
  /** Reactive: the rail is showing (`lg`) AND collapsed. Always `false` below `lg`. */
  readonly collapsed: () => boolean;
  /**
   * Flip the stored choice and remember it. Answers whether it acted: `false`
   * below `lg`, where it changes and writes nothing, so a caller consumes the
   * key only when the toggle did something.
   */
  readonly toggle: () => boolean;
};

export function createRailCollapse(): RailCollapse {
  const atLg = createRailViewport();
  const [stored, setStored] = createSignal(
    readLocalPref(RAIL_COLLAPSE_KEY, RailCollapsedSchema, false),
  );
  return {
    collapsed: () => atLg() && stored(),
    toggle: () => {
      if (!atLg()) return false;
      const next = !stored();
      setStored(next);
      writeLocalPref(RAIL_COLLAPSE_KEY, next);
      return true;
    },
  };
}
